import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { FILTER_PARAMS, filterSearchKey } from 'components/zpFilter/filterUtils';
import { useInnerWidth } from 'hooks';
import { dispatchSessionShowMoreSignupModal } from 'store/modals/actions';

import Body from './body';
import { formatData, SORT_DIRECTIONS, sortBody } from './common';
import Head from './head';

import classes from './table.module.css';

export default function Table({
  columnConfig,
  bodyData,
  customShowMore,
  showMoreText,
  defaultRowsDisplay,
  displayRowsPerExpand,
  hideRows,
  scrollOnMobile,
  wrapTitleOnMobile,
  onLinkClick,
  tableHeaderTitleContent = undefined,
  columnWidthBalanced,
}) {
  const dispatch = useDispatch();
  const width = useInnerWidth();

  const [showCaret, setShowCaret] = useState(false);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [rowsCount, setRowsCount] = useState(defaultRowsDisplay);
  const [hasCustomRenderColumn, setHasCustomRenderColumn] = useState(false)

  const isMobile = useMemo(() => width < 768, [width]);

  useEffect(() => {
    setRowsCount(defaultRowsDisplay);
  }, [defaultRowsDisplay]);

  const initializeData = useMemo(() => {
    let newData = formatData(columnConfig, bodyData || [], width < 768);
    if (sortColumn && sortDirection) {
      newData = sortBody(newData, sortColumn, sortDirection);
    }
    return newData || [];
  }, [bodyData]);

  useEffect(() => {
    for (let cfg in columnConfig) {
      if (columnConfig[cfg]?.hasOwnProperty("customRender")) {
        setHasCustomRenderColumn(true);
        break;
      }
    }

  }, [columnConfig])


  const [data, setData] = useState(initializeData);

  const onSort = (column, direction) => {
    const newSortDirection =
      direction ||
      (sortDirection === SORT_DIRECTIONS.ASC ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC);

    setData(sortBody(data, column, newSortDirection));
    setSortColumn(column);
    setSortDirection(newSortDirection);
  };

  const showRows = useCallback(() => {
    setRowsCount((prev) => prev + displayRowsPerExpand);
    dispatch(dispatchSessionShowMoreSignupModal());
  }, [displayRowsPerExpand]);

  const onCellClick = useCallback(
    (rowIdx, columnIdx) => (event) => {
      const rowData = data?.[rowIdx]?.rowValue || {};
      const { city, stateCode, title, jobTitle } = rowData;
      const filterTitle = title || jobTitle;

      if (onLinkClick) {
        onLinkClick(rowData, event, rowIdx, columnIdx);
        return;
      }

      const filterValues = {};

      const locations =
        city && stateCode
          ? [
            {
              city,
              state: stateCode,
            },
          ]
          : [];

      if (locations.length) filterValues[FILTER_PARAMS.LOCATION] = locations;

      if (filterTitle && !city) filterValues[FILTER_PARAMS.JOB_TITLE] = [filterTitle];

      localStorage.setItem(filterSearchKey, JSON.stringify(filterValues));
    },
    [data]
  );

  useEffect(() => {
    setShowCaret(true);
  }, []);

  useEffect(() => {
    if (!_isEqual(bodyData, data)) {
      let newData = formatData(columnConfig, bodyData, isMobile);

      if (sortColumn && sortDirection) {
        newData = sortBody(newData, sortColumn, sortDirection);
      }

      setData(newData);

      if (!_isEqual(data, newData) && !hasCustomRenderColumn) {
        setRowsCount(defaultRowsDisplay);
      }
    }
  }, [bodyData, isMobile]);

  return (
    <div>
      {tableHeaderTitleContent}
      <div
        className={classnames({
          [classes.scrollable]: scrollOnMobile,
        })}
      >
        <table
          className={classnames(classes.table, 'custom-table', {
            [classes.colsWidthBalanced]: columnWidthBalanced,
          })}
        >
          <Head
            config={columnConfig}
            showCaret={showCaret}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            isMobile={isMobile}
            onColClick={onSort}
            wrapTitleOnMobile={wrapTitleOnMobile}
          />
          <Body
            names={Object.keys(columnConfig)}
            data={data.slice(0, !hideRows ? rowsCount : data.length)}
            isMobile={isMobile}
            rowsCount={rowsCount}
            hideRows={hideRows}
            onCellClick={onCellClick}
          />
        </table>
      </div>

      {rowsCount < data?.length && !customShowMore && (
        <div
          data-testid="table-show-more"
          className={classnames(
            'text-capitalize',
            'list-link',
            'z-mt-16',
            'semi-bold-font',
            'show-more-button',
            classes['show-more']
          )}
          onClick={showRows}
        >
          {showMoreText}
        </div>
      )}
    </div>
  );
}

// classnames('list-link', 'z-mt-16', 'semi-bold-font', 'show-more-button', classes['show-more'])

Table.propTypes = {
  bodyData: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnConfig: PropTypes.objectOf(PropTypes.object).isRequired,
  defaultRowsDisplay: PropTypes.number,
  displayRowsPerExpand: PropTypes.number,
  hideRows: PropTypes.bool, // if true, the remaining rows from (defaultRowsDisplay) gets shown on html but has "d-none" class --- FOR SEO purposes
  customShowMore: PropTypes.bool,
  showMoreText: PropTypes.string,
  onLinkClick: PropTypes.func,
  scrollOnMobile: PropTypes.bool,
  wrapTitleOnMobile: PropTypes.bool,
  columnWidthBalanced: PropTypes.bool,
  rowHeight: PropTypes.string,
  rowHeightMobile: PropTypes.string,
};

Table.defaultProps = {
  defaultRowsDisplay: 5,
  displayRowsPerExpand: 5,
  salaryTable: false,
  regularTable: false,
  customShowMore: false,
  showMoreText: 'Show more',
  listView: false,
  scrollOnMobile: true,
  wrapTitleOnMobile: true,
  columnWidthBalanced: true,
};
